import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  THEMES,
  type AppearanceMode,
  type AppearanceView,
  type SessionView,
  type ThemeId,
} from '@wodi/shared';
import {
  Button,
  LoadingBlock,
  Notice,
  PageHeader,
  Section,
  SegmentedControl,
  ThemeCards,
  type ThemeCardOption,
} from '@wodi/ui';
import { useState } from 'react';

import { appearanceQuery, updateAppearance } from '../api/appearance.ts';
import { languageQuery, updateLanguage } from '../api/language.ts';
import { ApiError } from '../api/client.ts';
import { sessionQuery } from '../api/session.ts';
import { applyAppearance, currentAppearance, resolvedMode } from '../appearance/apply.ts';
import { useOnline } from '../hooks/use-online.ts';
import { errorText, isLanguage, useI18n } from '../i18n/i18n.tsx';
import { screen } from '../layout/screen.tsx';
import { toasts } from '../toasts.ts';
import { PasswordSection } from './account-password.tsx';

// Account (ADR 0030): what each person sets for themselves. For now that is how Wodi
// looks: a colour theme and light or dark. A change applies at once and is saved to the
// person's account, so it follows them to any computer they sign in on.

/** The theme card value for "use the facility default". Theme IDs never take this form. */
const FACILITY_DEFAULT = 'facility-default';
const DEVICE = 'device';

const MODE_OPTIONS = [
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
  { id: DEVICE, label: 'Match my device' },
] as const;

function themeLabel(id: ThemeId): string {
  return THEMES.find((t) => t.id === id)?.label ?? id;
}

function themeOptions(facilityDefault: ThemeId): ThemeCardOption[] {
  return [
    {
      id: FACILITY_DEFAULT,
      theme: facilityDefault,
      label: 'Facility default',
      description: `${themeLabel(facilityDefault)}, chosen by your administrator.`,
    },
    ...THEMES.map((t) => ({ id: t.id, theme: t.id, label: t.label, description: t.description })),
  ];
}

/** Keeps the session's copy in step, so the whole app shows the saved appearance. */
function rememberInSession(
  queryClient: ReturnType<typeof useQueryClient>,
  saved: AppearanceView,
): void {
  queryClient.setQueryData(appearanceQuery.queryKey, saved);
  queryClient.setQueryData<SessionView | null>(sessionQuery.queryKey, (session) =>
    session === null || session === undefined
      ? session
      : { ...session, appearance: saved.effective },
  );
}

function AppearanceForm({ appearance, online }: { appearance: AppearanceView; online: boolean }) {
  const queryClient = useQueryClient();
  // What the person picked, shown at once while it saves.
  const [draft, setDraft] = useState({ theme: appearance.theme, mode: appearance.mode });
  const [shownMode, setShownMode] = useState(resolvedMode(appearance.effective.mode));

  const save = useMutation({
    mutationFn: updateAppearance,
    onMutate: (next) => {
      const before = currentAppearance();
      const effective = {
        theme: next.theme ?? appearance.facilityDefaultTheme,
        mode: next.mode ?? ('system' as const),
      };
      applyAppearance(effective);
      setShownMode(resolvedMode(effective.mode));
      return { before };
    },
    onSuccess: (saved) => {
      rememberInSession(queryClient, saved);
      toasts.show({ title: 'Appearance saved' });
    },
    onError: (_error, _next, context) => {
      // Put back what was on screen, so the screen and the account agree.
      if (context !== undefined) {
        applyAppearance(context.before);
        setShownMode(resolvedMode(context.before.mode));
      }
      setDraft({ theme: appearance.theme, mode: appearance.mode });
    },
  });

  const choose = (next: { theme: ThemeId | null; mode: AppearanceMode | null }) => {
    setDraft(next);
    save.mutate(next);
  };

  return (
    <div className={screen.page}>
      {online ? null : (
        <Notice tone="info">You're offline. Appearance can be changed once you reconnect.</Notice>
      )}
      {save.error === null ? null : (
        <Notice tone="error">
          {save.error instanceof ApiError
            ? save.error.message
            : "Your appearance wasn't saved. Try again."}
        </Notice>
      )}
      <ThemeCards
        label="Theme"
        description="Colours only: words, shapes and danger red mean the same in every theme."
        options={themeOptions(appearance.facilityDefaultTheme)}
        value={draft.theme ?? FACILITY_DEFAULT}
        mode={shownMode}
        isDisabled={!online || save.isPending}
        onChange={(id) => {
          const theme = id === FACILITY_DEFAULT ? null : (id as ThemeId);
          if (theme !== draft.theme) choose({ theme, mode: draft.mode });
        }}
      />
      <SegmentedControl
        label="Mode"
        options={MODE_OPTIONS}
        value={draft.mode ?? DEVICE}
        isDisabled={!online || save.isPending}
        onChange={(id) => {
          const mode = id === DEVICE ? null : (id as AppearanceMode);
          if (mode !== draft.mode) choose({ theme: draft.theme, mode });
        }}
      />
    </div>
  );
}

function AppearanceSection() {
  const online = useOnline();
  const appearance = useQuery(appearanceQuery);
  return (
    <Section
      title="Appearance"
      description="How Wodi looks for you, on any computer you sign in on. Prints are always black on white."
    >
      {appearance.isPending ? <LoadingBlock label="Loading your appearance" rows={3} /> : null}
      {appearance.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense isDisabled={!online} onPress={() => void appearance.refetch()}>
              Try again
            </Button>
          }
        >
          {online
            ? "Your appearance couldn't be loaded. Try again."
            : "You're offline. Your appearance can be loaded once you reconnect."}
        </Notice>
      ) : null}
      {appearance.data === undefined ? null : (
        // Starts again from the account's values whenever they change elsewhere.
        <AppearanceForm
          key={`${appearance.data.theme ?? ''}:${appearance.data.mode ?? ''}`}
          appearance={appearance.data}
          online={online}
        />
      )}
    </Section>
  );
}

/**
 * The interface language (docs/plans/phase3-portal-feedback-kiswahili.md §8). Saved to the
 * person's account, so it follows them; it changes the shell at once.
 */
function LanguageSection() {
  const online = useOnline();
  const queryClient = useQueryClient();
  const { t, language, setLanguage } = useI18n();
  const saved = useQuery(languageQuery);
  const save = useMutation({
    mutationFn: updateLanguage,
    onMutate: (next) => {
      const before = language;
      setLanguage(next);
      return { before };
    },
    onSuccess: (result) => {
      queryClient.setQueryData(languageQuery.queryKey, result);
      queryClient.setQueryData<SessionView | null>(sessionQuery.queryKey, (session) =>
        session === null || session === undefined
          ? session
          : { ...session, language: result.language ?? 'en' },
      );
      toasts.show({ title: t('language.saved') });
    },
    onError: (_error, _next, context) => {
      if (context !== undefined) setLanguage(context.before);
    },
  });
  return (
    <Section title={t('language.label')} description={t('language.description')}>
      {saved.isPending ? <LoadingBlock label={t('common.loading')} rows={1} /> : null}
      {saved.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense isDisabled={!online} onPress={() => void saved.refetch()}>
              {t('common.tryAgain')}
            </Button>
          }
        >
          {errorText(t, language, saved.error)}
        </Notice>
      ) : null}
      {save.error === null ? null : (
        <Notice tone="error">{errorText(t, language, save.error)}</Notice>
      )}
      {saved.data === undefined ? null : (
        <div className={screen.page}>
          <SegmentedControl
            label={t('language.label')}
            options={[
              { id: 'en', label: t('language.en') },
              { id: 'sw', label: t('language.sw') },
            ]}
            value={language}
            isDisabled={!online || save.isPending}
            onChange={(id) => {
              if (isLanguage(id) && id !== language) save.mutate(id);
            }}
          />
          {language === 'sw' ? <Notice tone="info">{t('language.machineNotice')}</Notice> : null}
        </div>
      )}
    </Section>
  );
}

export function AccountPage() {
  const { t } = useI18n();
  return (
    <>
      <PageHeader title="Account" description={t('page.accountDescription')} />
      <AppearanceSection />
      <LanguageSection />
      <PasswordSection />
    </>
  );
}
